<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { projectsApi, type Project } from '@/services/api/projects';
import { useAppConfirm } from '@/composables/confirm';
import { useNotifier } from '@/composables/notifier';

const projects = ref<Project[]>([]);
const search = ref('');
const confirm = useAppConfirm();
const notify = useNotifier();

async function reload(): Promise<void> {
  projects.value = await projectsApi.index();
}

async function doSearch(): Promise<void> {
  if (search.value.length === 0) {
    await reload();
    return;
  }
  const result = await projectsApi.search(search.value);
  projects.value = Array.isArray(result) ? result : result.data;
}

async function clone(project: Project): Promise<void> {
  const cloned = await projectsApi.clone(project.id);
  projects.value = [...projects.value, cloned];
  notify.success('Project copied');
}

async function remove(project: Project): Promise<void> {
  const ok = await confirm({
    header: 'Remove Project',
    message: `Remove project "${project.title}"?`,
  });
  if (!ok) return;
  await projectsApi.remove(project.id);
  projects.value = projects.value.filter((p) => p.id !== project.id);
}

onMounted(reload);
</script>

<template>
  <section class="resource">
    <header class="resource__header">
      <h1>Projects</h1>
      <RouterLink :to="{ name: 'projectsAdd' }" class="resource__cta">+ New Project</RouterLink>
    </header>

    <form class="resource__new" @submit.prevent="doSearch">
      <input v-model="search" type="text" placeholder="Search projects…" />
      <button type="submit">Search</button>
    </form>

    <ul class="resource__list">
      <li v-for="p in projects" :key="p.id">
        <RouterLink :to="`/projects/${p.id}/forms`">{{ p.title }}</RouterLink>
        <button type="button" @click="clone(p)">Clone</button>
        <button type="button" @click="remove(p)">Remove</button>
      </li>
    </ul>
    <p v-if="projects.length === 0">No projects.</p>
  </section>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.resource__cta {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border-radius: 0.375rem;
  text-decoration: none;
}
</style>
